/**
 * Packing Slip HTML Generator for APO/FPO/DPO Military Exchange Returns
 *
 * Generates a printable HTML document with static estimation-based pagination.
 * Used via:
 *   - Native app: NATIVE_APP.PRINT_BASE64 (base64-encoded HTML)
 *   - Web: window.open() + window.print()
 *
 * @see src/utils/printReturnLabelUtil.ts for the existing print pattern
 * @see docs/MYACCR-2228-spike-notes.md for spike findings
 */

import { isSsr } from '@src/utils/general';
import type { PackingSlipData, Page } from './types';
import { STYLES } from './styles';
import { paginateItems } from './pagination';
import {
  renderHeader,
  renderOrderInfo,
  renderAssociateNotice,
  renderScreenTable,
  renderItemsTable,
  renderViewMore,
  renderReturnInstructions,
} from './templates';

// ---------------------------------------------------------------------------
// Page assembly
// ---------------------------------------------------------------------------

function renderPage(page: Page, data: PackingSlipData): string {
  const parts: string[] = [];

  parts.push('<div class="page">');

  if (page.isFirstPage) {
    parts.push(renderHeader());
    parts.push(renderOrderInfo(data.orderId, data.orderBarcode));
    parts.push(renderAssociateNotice());
    // Screen-only: single continuous table with all items
    parts.push(renderScreenTable(data.items));
  } else {
    // Repeated header/order — visible only when printing
    parts.push(`<div class="page-repeat-header">${renderHeader()}</div>`);
    parts.push(
      `<div class="page-repeat-order">${renderOrderInfo(
        data.orderId,
        data.orderBarcode,
      )}</div>`,
    );
  }

  if (page.items.length > 0) {
    parts.push(renderItemsTable(page.items, !page.isFirstPage));
  }

  if (page.hasMore) {
    parts.push(renderViewMore());
  }

  if (page.isLastPage) {
    parts.push(renderReturnInstructions(data.orderId));
  }

  parts.push('</div>');
  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Main HTML generation
// ---------------------------------------------------------------------------

/**
 * Generates a full HTML document for the APO/FPO/DPO packing slip.
 * Handles multi-page pagination via JS-based page splitting.
 */
export function createPackingSlipHtml(data: PackingSlipData): string {
  const pages = paginateItems(data.items);
  const pageHtml = pages.map((page) => renderPage(page, data)).join('\n');

  return `
<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Packing Slip | Order #${data.orderId}</title>
    <style>${STYLES}</style>
  </head>
  <body>
    ${pageHtml}
    <script>
      // Delay print to allow barcode image to render
      setTimeout(function() { window.print(); }, 1000);
    </script>
  </body>
</html>
  `.trim();
}

// ---------------------------------------------------------------------------
// Print triggers (mirror existing patterns from printReturnLabelUtil.ts)
// ---------------------------------------------------------------------------

/**
 * Sends the packing slip to the native app's print dialog via
 * LIFE_CYCLE_EVENT_BUS → NATIVE_APP.PRINT_BASE64
 */
export function printPackingSlipNative(data: PackingSlipData): void {
  if (isSsr) return;
  const html = createPackingSlipHtml(data);
  const base64Html = btoa(html);
  window?.LIFE_CYCLE_EVENT_BUS?.trigger('NATIVE_APP.PRINT_BASE64', {
    base64HtmlData: base64Html,
  });
}

/**
 * Opens the packing slip in a new browser tab and triggers print.
 */
export function printPackingSlipBrowser(data: PackingSlipData): void {
  if (isSsr) return;
  const html = createPackingSlipHtml(data);
  const newWindow = window.open(
    '',
    '_blank',
    'top=0,left=0,height=750px,width=auto,scrollbars=yes,resizable=yes',
  );
  if (newWindow) {
    newWindow.document.open();
    newWindow.document.write(html);
    newWindow.document.close();
  }
}
